import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { noop } from 'ui-components/default-props/noop';
import { Text, TextType } from 'ui-components/components/text';

import { ENHANCED_RETURNS_MODAL_PATH } from '../../../../enhanced-returns/activation-modal/modal/enhanced-returns-activation-step-detective';
import { EventSourceStrings, Workflows } from '../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { useReturnsTabContext } from '../hooks/use-returns-tab-context';

import messages from './messages';
import { ButtonWithLockIcon } from '../../../../../../feature-gates/button-with-lock-icon';

/**
 * Returns tab enhanced action button component
 * Displays the appropriate action button for enhanced returns based on current state
 * @typedef {object} ReturnsTabEnhancedActionButtonProps
 * @property {() => void} onClick - Function to call when the button is clicked
 * @property {boolean} [isLoading] - Whether the button should show a loading state
 * @param {ReturnsTabEnhancedActionButtonProps} props - Component props
 * @returns {React.ReactElement} The Returns tab enhanced action button
 */
export const ActionButton = ({ onClick, isLoading }) => {
  const location = useLocation();
  const history = useHistory();
  const trackActivateEnhancedReturns = useTrackWorkflow(Workflows.ACTIVATE_ENHANCED_RETURNS_FLOW);
  const { isEnhancedReturnsFeatureEnabled, isStoreSetupCompleteForEnhancedReturns } = useReturnsTabContext();

  const onClickActivateEnhancedReturns = () => {
    trackActivateEnhancedReturns(WorkflowSteps.START, {
      source: EventSourceStrings.STORE_SETTINGS_RETURNS_TAB,
    });

    history.push(ENHANCED_RETURNS_MODAL_PATH, {
      redirectUrl: location.pathname,
    });
  };

  const buttonProperties = () => {
    if (isStoreSetupCompleteForEnhancedReturns) {
      return {
        isLoading,
        onClick,
        text: messages.deactivateLink,
        disabled: false,
        disabledTooltipMessage: null,
        theme: 'link',
      };
    }

    if (isEnhancedReturnsFeatureEnabled) {
      return {
        isLoading,
        onClick: onClickActivateEnhancedReturns,
        text: messages.activateButton,
        disabled: false,
        disabledTooltipMessage: null,
        theme: 'primary',
      };
    }

    if (!isEnhancedReturnsFeatureEnabled) {
      return {
        isLoading,
        onClick: noop,
        text: messages.activateButton,
        disabled: true,
        disabledTooltipMessage: messages.notSupportedTooltipMessage,
        theme: 'primary',
      };
    }
  };

  const { onClick: onClickButton, text, disabled, theme, disabledTooltipMessage } = buttonProperties();

  return (
    <ButtonWithLockIcon
      disabled={disabled}
      disabledTooltipMessage={disabledTooltipMessage}
      isLoading={isLoading}
      onClick={onClickButton}
      theme={theme}
    >
      <Text textType={TextType.Paragraph} value={text} />
    </ButtonWithLockIcon>
  );
};
